import { useHistory } from 'react-router-dom'

import { PlanPeriodDto, getRenewalPlanPeriod } from 'core'
import { User } from 'core/application/account/loginUser'
import { BenefitDesignStatus } from 'core/domain/benefit/benefitDesignStatus'

import { Button, Card, Col, Row, Skeleton } from 'common/components'
import { formatYear, parseZoneDayMonthYearFormat } from 'common/dateFormatters'
import { useQueryRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import './styles.less'

type GchraAdminCountdownTimerProps = {
  planPeriod: PlanPeriodDto
  user: User
}

function GchraAdminCountdownTimer({ ...props }: GchraAdminCountdownTimerProps) {
  const history = useHistory()

  const { data: renewalPlanPeriod, isLoading: isRenewalPlanPeriodLoading } = useQueryRequestV2({
    queryFn: () => {
      return getRenewalPlanPeriod()
    },
  })

  const renewalPlanPeriodStartDate = formatYear(renewalPlanPeriod?.startDate)
  const renewalPlanPeriodEndDate = formatYear(renewalPlanPeriod?.endDate)
  const benefitDuration =
    renewalPlanPeriodStartDate !== renewalPlanPeriodEndDate
      ? renewalPlanPeriodStartDate + '-' + renewalPlanPeriodEndDate
      : renewalPlanPeriodStartDate

  return (
    <Skeleton loading={isRenewalPlanPeriodLoading}>
      {(renewalPlanPeriod === null ||
        (renewalPlanPeriod != null && renewalPlanPeriod.status !== BenefitDesignStatus.Signed)) && (
        <Card style={{ backgroundColor: '#E6FFFF', border: '1px solid #8DF3FC' }}>
          <Row gutter={16}>
            <Col xxl={18} xl={16} lg={13} md={13} sm={13} xs={24}>
              <h4>
                {renewalPlanPeriod?.notificationDate
                  ? `Your benefit is ready for ${benefitDuration}`
                  : 'Renew your benefit plan now'}
              </h4>
              {renewalPlanPeriod?.notificationDate ? (
                <p>
                  Hi {props.user?.firstName}, you’re all set for {benefitDuration}! We’ll automatically launch your
                  benefit on {parseZoneDayMonthYearFormat(renewalPlanPeriod?.startDate)} with your plan elections and
                  notify your employees. However, if you want to make changes, you still can until{' '}
                  {parseZoneDayMonthYearFormat(renewalPlanPeriod?.startDate)}. View your {benefitDuration} benefit
                  settings by going to Benefit Details.
                </p>
              ) : (
                <p>
                  Hi {props.user?.firstName}. If you want to change your benefit elections for {benefitDuration}, such
                  as increasing your allowance amounts, our simple tool will walk you through the process.
                </p>
              )}

              <Row gutter={16} className="mb-24 mt-24">
                <Col>
                  {renewalPlanPeriod !== null && !renewalPlanPeriod.notificationDate && (
                    <Button
                      type="primary"
                      onClick={() => {
                        history.push(ROUTE_PATHS.RENEWAL_TASKS)
                      }}
                    >
                      {renewalPlanPeriod.status != BenefitDesignStatus.Completed
                        ? 'Renew benefit plan'
                        : 'In Progress - Continue where you left off'}
                    </Button>
                  )}

                  {renewalPlanPeriod !== null && renewalPlanPeriod.notificationDate && (
                    <Button
                      type="primary"
                      onClick={() => {
                        history.push(`${ROUTE_PATHS.BENEFIT_DETAILS_PATH}?planPeriodId=${renewalPlanPeriod.id}`)
                      }}
                    >
                      Make changes to the {benefitDuration} benefit
                    </Button>
                  )}
                </Col>
              </Row>
              <p>
                {renewalPlanPeriod?.notificationDate ? (
                  <b>View your {benefitDuration} benefit settings by going to Benefit Details.</b>
                ) : (
                  <b>
                    If you take no action, we’ll automatically launch your benefit on{' '}
                    {parseZoneDayMonthYearFormat(renewalPlanPeriod?.startDate)} with your current plan elections. After
                    launch, you can make limited adjustments to your benefit design.
                  </b>
                )}
              </p>
            </Col>
          </Row>
        </Card>
      )}
    </Skeleton>
  )
}

export default GchraAdminCountdownTimer
